import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Topbar } from "../components/shell/Topbar";
import { useLayoutContext } from "../layouts/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { cn } from "@/packages/utils/cn";

interface Mock {
  id: string;
  projectId: string;
  name: string;
  description: string | null;
  method: string;
  endpoint: string;
  responseBody: string;
  responseHeaders: Record<string, string> | null;
  statusCode: number;
  latencyMs: number;
  enabled: boolean;
  requestCount: number;
  lastUsedAt: string | null;
}

interface Stat {
  bucket: string;
  count: number;
  ts: string;
}

const METHOD_TONES: Record<string, string> = {
  GET: "bg-signal-ok-soft text-signal-ok",
  POST: "bg-signal-warn-soft text-signal-warn",
  PUT: "bg-signal-lavender-soft text-signal-lavender",
  PATCH: "bg-signal-lavender-soft text-signal-lavender",
  DELETE: "bg-signal-red-soft text-signal-red",
};

const STATUS_CODES = [200, 201, 204, 400, 404, 500];

export function ApiMocksPage() {
  const { teamId, projectId } = useParams();
  const { teams, projects } = useLayoutContext();
  const team = teams.find((t) => t.id === teamId);
  const project = (projects[teamId ?? ""] ?? []).find((p) => p.referenceId === projectId);

  const [mocks, setMocks] = useState<Mock[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [stats, setStats] = useState<Stat[]>([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Mock | null>(null);
  const [saving, setSaving] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const refresh = async () => {
    if (!projectId) return;
    const list: Mock[] = await fetch(`/api/projects/${projectId}/mocks`).then(
      (r) => r.json(),
    );
    setMocks(list);
    if (!selectedId && list.length > 0) {
      setSelectedId(list[0]!.id);
      setDraft(list[0]!);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (!selectedId || !projectId) return;
    const m = mocks.find((x) => x.id === selectedId);
    if (m) setDraft(m);
    fetch(`/api/projects/${projectId}/mocks/${selectedId}/stats`)
      .then((r) => r.json())
      .then(setStats);
  }, [selectedId, mocks, projectId]);

  if (!team || !project) return null;

  const baseHost =
    window.location.host.replace(/^[^.]+\./, "") || window.location.host;
  const endpointPath = (draft?.endpoint ?? "").replace(/^\/+/, "");
  const mockUrl = draft
    ? `${window.location.protocol}//${team.referenceId}-${project.slug}.${baseHost}/api/mock/${endpointPath}`
    : "";

  const copyUrl = async () => {
    if (!mockUrl) return;
    await navigator.clipboard.writeText(mockUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 1500);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    await fetch(`/api/projects/${projectId}/mocks/${draft.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: draft.name,
        description: draft.description,
        method: draft.method,
        endpoint: draft.endpoint,
        responseBody: draft.responseBody,
        responseHeaders: draft.responseHeaders ?? {},
        statusCode: draft.statusCode,
        latencyMs: draft.latencyMs,
        enabled: draft.enabled,
      }),
    });
    setSaving(false);
    refresh();
  };

  const remove = async () => {
    if (!draft || !confirm("Delete this mock?")) return;
    await fetch(`/api/projects/${projectId}/mocks/${draft.id}`, {
      method: "DELETE",
    });
    setSelectedId(null);
    setDraft(null);
    refresh();
  };

  const maxCount = Math.max(1, ...stats.map((s) => s.count));

  return (
    <>
      <Topbar
        title={draft?.name ?? "API mocks"}
        crumbs={[
          { label: team.name, to: `/teams/${team.id}` },
          { label: project.name, to: `/teams/${team.id}/projects/${project.referenceId}` },
          { label: "Mocks" },
        ]}
        actions={
          <>
            {draft && (
              <Button size="sm" variant="outline" onClick={remove}>
                Delete
              </Button>
            )}
            {draft && (
              <Button size="sm" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
              + New mock
            </Button>
          </>
        }
      />

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[280px_1fr_320px] overflow-hidden">
        {/* List */}
        <aside className="border-r border-rule overflow-auto bg-card-paper">
          <div className="px-4 py-3 border-b border-rule flex justify-between items-center">
            <span className="text-eyebrow">Mocks · {mocks.length}</span>
            <button
              onClick={() => setCreating(true)}
              className="font-mono text-xs text-signal-red"
            >
              + new
            </button>
          </div>
          {mocks.map((m) => {
            const sel = m.id === selectedId;
            return (
              <button
                key={m.id}
                onClick={() => setSelectedId(m.id)}
                className={cn(
                  "block w-full text-left px-4 py-3 border-b border-rule-soft border-l-2 relative",
                  sel
                    ? "bg-panel border-l-signal-red"
                    : "border-l-transparent hover:bg-panel/40",
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={cn(
                      "px-1.5 py-0.5 rounded text-[9.5px] font-mono font-bold",
                      METHOD_TONES[m.method] ?? "bg-panel text-foreground",
                    )}
                  >
                    {m.method}
                  </span>
                  <span
                    className={cn(
                      "size-1.5 rounded-full ml-auto",
                      m.enabled ? "bg-signal-ok" : "bg-faint",
                    )}
                  />
                </div>
                <div className="font-mono text-xs font-medium truncate">
                  {m.endpoint}
                </div>
                <div className="font-mono text-[10px] text-faint mt-0.5">
                  {m.requestCount} hits · {m.latencyMs}ms
                </div>
              </button>
            );
          })}
        </aside>

        {/* Editor */}
        <div className="overflow-auto flex flex-col">
          {!draft ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              Select a mock or create one.
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-rule bg-card-paper space-y-3">
                <div>
                  <div className="text-eyebrow mb-1">Hit URL</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 font-mono text-xs text-foreground bg-panel border border-rule rounded-md px-2 py-1.5 block break-all">
                      {mockUrl}
                    </code>
                    <Button size="sm" variant="outline" onClick={copyUrl}>
                      {copiedUrl ? "Copied" : "Copy"}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-[100px_1fr] gap-2">
                  <select
                    value={draft.method}
                    onChange={(e) => setDraft({ ...draft, method: e.target.value })}
                    className="border border-rule rounded-md px-2 py-1.5 bg-card-paper font-mono text-xs"
                  >
                    {Object.keys(METHOD_TONES).map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                  <Input
                    value={draft.endpoint}
                    onChange={(e) =>
                      setDraft({ ...draft, endpoint: e.target.value })
                    }
                    className="font-mono text-xs"
                    placeholder="api/v2/customer/:id"
                  />
                </div>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Friendly name"
                />
              </div>

              <div className="flex-1 px-6 py-4">
                <div className="text-eyebrow mb-2">
                  Response body · application/json
                </div>
                <Textarea
                  value={draft.responseBody}
                  onChange={(e) =>
                    setDraft({ ...draft, responseBody: e.target.value })
                  }
                  rows={20}
                  className="font-mono text-xs"
                />
              </div>
            </>
          )}
        </div>

        {/* Settings rail */}
        {draft && (
          <aside className="border-l border-rule bg-panel/40 overflow-auto p-5 space-y-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-eyebrow">Enabled</span>
                <Switch
                  checked={draft.enabled}
                  onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                />
              </div>
            </div>
            <div>
              <div className="text-eyebrow mb-2">Status code</div>
              <div className="flex gap-1.5 flex-wrap">
                {STATUS_CODES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setDraft({ ...draft, statusCode: c })}
                    className={cn(
                      "px-2.5 py-1 rounded font-mono text-xs border",
                      draft.statusCode === c
                        ? "border-foreground bg-ink text-paper font-bold"
                        : "border-rule bg-card-paper",
                    )}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-eyebrow mb-2">Latency</div>
              <div className="bg-card-paper border border-rule rounded-md p-3">
                <div className="flex justify-between font-mono text-xs text-muted-foreground mb-2">
                  <span>{draft.latencyMs} ms</span>
                  <span className="text-faint">simulated</span>
                </div>
                <Slider
                  value={[draft.latencyMs]}
                  onValueChange={([v]) =>
                    setDraft({ ...draft, latencyMs: v ?? 0 })
                  }
                  max={3000}
                  step={50}
                />
              </div>
            </div>
            <div>
              <div className="text-eyebrow mb-2">Recent calls · 24h</div>
              <div className="bg-card-paper border border-rule rounded-md p-3">
                <div className="flex justify-between font-mono text-xs mb-2">
                  <span className="text-muted-foreground">Total</span>
                  <span className="font-semibold">
                    {stats.reduce((a, b) => a + b.count, 0)}
                  </span>
                </div>
                <div className="flex items-end gap-0.5 h-9">
                  {stats.map((s) => (
                    <div
                      key={s.bucket}
                      className="flex-1 bg-signal-ok rounded-sm"
                      style={{
                        height: `${(s.count / maxCount) * 100}%`,
                        minHeight: s.count > 0 ? "2px" : "0",
                      }}
                      title={`${s.count} hits`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </aside>
        )}
      </div>

      {creating && (
        <CreateMockDialog
          projectId={projectId!}
          onClose={() => setCreating(false)}
          onCreated={(mock) => {
            setCreating(false);
            setSelectedId(mock.id);
            refresh();
          }}
        />
      )}
    </>
  );
}

function CreateMockDialog({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: string;
  onClose: () => void;
  onCreated: (m: Mock) => void;
}) {
  const [name, setName] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [method, setMethod] = useState("GET");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`/api/projects/${projectId}/mocks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name || endpoint,
          method,
          endpoint,
          responseBody: '{\n  "ok": true\n}',
          statusCode: 200,
        }),
      });
      if (!res.ok) {
        setError((await res.json()).error ?? "Failed");
        return;
      }
      onCreated(await res.json());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New mock</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-[100px_1fr] gap-2">
            <div className="space-y-1.5">
              <Label>Method</Label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full border border-rule rounded-md px-2 py-1.5 bg-card-paper font-mono text-xs"
              >
                {Object.keys(METHOD_TONES).map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Endpoint</Label>
              <Input
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                placeholder="api/v2/customer"
                className="font-mono text-xs"
              />
            </div>
          </div>
          {error && <div className="text-sm text-signal-red">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!endpoint || submitting}>
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="size-3.5 animate-spin" />
                Creating…
              </span>
            ) : (
              "Create"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
